import React, { useState } from 'react'

import Toast, { TOAST_TYPE } from 'components/Toast/Toast'
import Upload from 'components/Forms/Upload/Upload'
import Input from 'components/Forms/Input/Input'
import Toggle from 'components/Forms/Toggle/Toggle'
import ColorPicker from 'components/Forms/ColorPicker/ColorPicker'
import FormBlock from 'components/Forms/FormBlock'
import LocaleFormBlock, { CONTROL_TYPES } from 'components/Forms/components/LocaleFormBlock/LocaleFormBlock'
import FormModal from 'components/Modal/FormModal/FormModal'
import Tags from 'components/Tags/Tags'
import MarkdownEditor from 'components/Forms/MarkdownEditor/MarkdownEditor'

import {
    fieldsValidator,
    getIsValueRule,
    getIsIntStringRule,
    getIsNotEmptyStringRule,
    getIsHexRule,
} from 'utils/fieldsValidator'
import { setKeyToObject, removeKeyFromObject } from 'utils/common'
import {
    createTranslation,
    getLocaleDependentTextFieldsStateObject,
    addLocaleDependentTextFieldsToValidator,
} from 'utils/translations'

import { DEFAULT_LOCALE } from 'constants/locales'

const LOCALE_DEPENDENT_FIELDS = {
    NAME: {
        fieldName: 'name',
    },
    DESCRIPTION: {
        fieldName: 'description',
    },
}

const CreateSectionModal = ({ onSubmit = () => {}, onClose = () => {} }) => {
    const [isLoading] = useState(false)
    const [activeNameLocale, setActiveNameLocale] = useState(DEFAULT_LOCALE)
    const [activeDescriptionLocale, setActiveDescriptionLocale] = useState(DEFAULT_LOCALE)

    const [localeDependentFields, setLocaleDependentFields] = useState(() =>
        getLocaleDependentTextFieldsStateObject(LOCALE_DEPENDENT_FIELDS),
    )

    const [isShowDescription, setIsShowDescription] = useState(true)

    const [isEnabled, setIsEnabled] = useState(true)

    const [icon, setIcon] = useState(null)
    const [iconUrl, setIconUrl] = useState('')
    const [image, setImage] = useState(null)
    const [imageUrl, setImageUrl] = useState('')
    const [collectionImage, setCollectionImage] = useState(null)
    const [collectionImageUrl, setCollectionImageUrl] = useState('')

    const [color, setColor] = useState('')

    const [customId, setCustomId] = useState('')

    const [tags, setTags] = useState([])

    const [orderNumber, setOrderNumber] = useState('')

    const [article, setArticle] = useState('')

    const [errors, setErrors] = useState({})

    const handleSubmit = async () => {
        try {
            setErrors({})

            const validator = new fieldsValidator()
            addLocaleDependentTextFieldsToValidator(validator, {
                fields: LOCALE_DEPENDENT_FIELDS,
                values: localeDependentFields,
                excludeFields: {
                    [LOCALE_DEPENDENT_FIELDS.DESCRIPTION.fieldName]: !isShowDescription,
                },
            })
            validator.addFields([
                validator.createField('orderNumber', String(orderNumber), [
                    getIsValueRule(),
                    getIsNotEmptyStringRule(),
                    getIsIntStringRule(),
                ]),
            ])

            if (color) {
                validator.addFields([
                    validator.createField('color', color, [
                        getIsValueRule(),
                        getIsNotEmptyStringRule('Field is required'),
                        getIsHexRule(),
                    ]),
                ])
            }

            if (icon) {
                validator.addFields([
                    validator.createField('icon', iconUrl, [
                        getIsValueRule(),
                        getIsNotEmptyStringRule('File must be uploaded'),
                    ]),
                ])
            }
            if (image) {
                validator.addFields([
                    validator.createField('image', imageUrl, [
                        getIsValueRule(),
                        getIsNotEmptyStringRule('File must be uploaded'),
                    ]),
                ])
            }
            if (collectionImage) {
                validator.addFields([
                    validator.createField('collectionImage', collectionImageUrl, [
                        getIsValueRule(),
                        getIsNotEmptyStringRule('File must be uploaded'),
                    ]),
                ])
            }

            const { isHasErrors, errors } = validator.validate()
            if (isHasErrors) {
                setErrors(errors)
                return
            }

            const nameTranslationId = await createTranslation(
                localeDependentFields[LOCALE_DEPENDENT_FIELDS.NAME.fieldName],
            )

            let descriptionTranslationId = null
            if (isShowDescription) {
                descriptionTranslationId = await createTranslation(
                    localeDependentFields[LOCALE_DEPENDENT_FIELDS.DESCRIPTION.fieldName],
                )
            }

            await onSubmit({
                id: null,
                nameTranslation: {
                    id: nameTranslationId,
                },
                descriptionTranslation: descriptionTranslationId ? { id: descriptionTranslationId } : null,
                enabled: isEnabled,
                iconUrl: iconUrl || null,
                imageUrl: imageUrl || null,
                collectionImageUrl: collectionImageUrl || null,
                customId: customId || null,
                color: color || null,
                orderNumber,
                tags,
                article,
            })

            return true
        } catch (err) {
            console.error(err)
            Toast(TOAST_TYPE.ERROR)
            throw err
        }
    }

    return (
        <FormModal
            headText="Create section"
            successText="Section created successfully!"
            actionText="Create"
            onAction={() => handleSubmit()}
            onClose={() => onClose()}
            isLoading={isLoading}
            width={740}
        >
            <LocaleFormBlock
                label="Name"
                errors={errors}
                fields={localeDependentFields}
                fieldName={LOCALE_DEPENDENT_FIELDS.NAME.fieldName}
                activeTabName={activeNameLocale}
                onChangeActiveTabName={v => setActiveNameLocale(v)}
                onChange={data => {
                    setLocaleDependentFields(data.value)
                    setErrors(data.errors)
                }}
            />
            <FormBlock>
                <Toggle
                    value={isShowDescription}
                    label="Show description"
                    onChange={value => {
                        setIsShowDescription(value)
                        setErrors(removeKeyFromObject(errors, 'isShowDescription'))
                    }}
                />
            </FormBlock>
            {isShowDescription && (
                <LocaleFormBlock
                    label="Description"
                    controlType={CONTROL_TYPES.TEXTAREA}
                    errors={errors}
                    fields={localeDependentFields}
                    fieldName={LOCALE_DEPENDENT_FIELDS.DESCRIPTION.fieldName}
                    activeTabName={activeDescriptionLocale}
                    onChangeActiveTabName={v => setActiveDescriptionLocale(v)}
                    onChange={data => {
                        setLocaleDependentFields(data.value)
                        setErrors(data.errors)
                    }}
                />
            )}
            <br />
            <FormBlock>
                <Toggle
                    value={isEnabled}
                    label="Enabled"
                    onChange={value => {
                        setIsEnabled(value)
                        setErrors(removeKeyFromObject(errors, 'isEnabled'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Icon">
                <Upload
                    value={icon}
                    error={errors.icon}
                    isUploaded={!!iconUrl}
                    onReset={() => {
                        setIconUrl('')
                        setIcon(null)
                    }}
                    onUpload={({ isSuccess, url, errorText }) => {
                        if (!isSuccess) {
                            setErrors(setKeyToObject(errors, 'icon', errorText))
                            return
                        }
                        setIconUrl(url)
                        setErrors(removeKeyFromObject(errors, 'icon'))
                    }}
                    onChange={file => {
                        setIconUrl('')
                        setIcon(file)
                        setErrors(removeKeyFromObject(errors, 'icon'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Image">
                <Upload
                    value={image}
                    error={errors.image}
                    isUploaded={!!imageUrl}
                    onReset={() => {
                        setImageUrl('')
                        setImage(null)
                    }}
                    onUpload={({ isSuccess, url, errorText }) => {
                        if (!isSuccess) {
                            setErrors(setKeyToObject(errors, 'image', errorText))
                            return
                        }
                        setImageUrl(url)
                        setErrors(removeKeyFromObject(errors, 'image'))
                    }}
                    onChange={file => {
                        setImageUrl('')
                        setImage(file)
                        setErrors(removeKeyFromObject(errors, 'image'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Collection image">
                <Upload
                    value={collectionImage}
                    error={errors.collectionImage}
                    isUploaded={!!collectionImageUrl}
                    onReset={() => {
                        setCollectionImageUrl('')
                        setCollectionImage(null)
                    }}
                    onUpload={({ isSuccess, url, errorText }) => {
                        if (!isSuccess) {
                            setErrors(setKeyToObject(errors, 'collectionImage', errorText))
                            return
                        }
                        setCollectionImageUrl(url)
                        setErrors(removeKeyFromObject(errors, 'collectionImage'))
                    }}
                    onChange={file => {
                        setCollectionImageUrl('')
                        setCollectionImage(file)
                        setErrors(removeKeyFromObject(errors, 'collectionImage'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Color">
                <ColorPicker value={color} error={errors.color} onChange={color => setColor(color.hex)} />
            </FormBlock>
            {/*<FormBlock label="Users Role">*/}
            {/*    <Select*/}
            {/*        error={errors.role}*/}
            {/*        value={role}*/}
            {/*        options={tagsData.role?.options}*/}
            {/*        onChange={value => {*/}
            {/*            setRole(value)*/}
            {/*            setErrors(removeKeyFromObject(errors, 'role'))*/}
            {/*        }}*/}
            {/*    />*/}
            {/*</FormBlock>*/}
            <FormBlock label="Tags">
                <Tags
                    isShowRoles
                    isForceSuccessClose={true}
                    isEnsureRemove={false}
                    selectedTagsList={tags}
                    onUpdate={tags => setTags(tags)}
                    onRemove={({ id }) => setTags(tags.filter(tag => tag.id !== id))}
                />
            </FormBlock>
            <FormBlock label="Custom ID">
                <Input
                    value={customId}
                    error={errors.customId}
                    onChange={value => {
                        setCustomId(value)
                        setErrors(removeKeyFromObject(errors, 'customId'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Order number">
                <Input
                    type="number"
                    min={1}
                    value={orderNumber}
                    error={errors.orderNumber}
                    onChange={value => {
                        setOrderNumber(value)
                        setErrors(removeKeyFromObject(errors, 'orderNumber'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Article">
                <MarkdownEditor value={article} onChange={setArticle} />
            </FormBlock>
        </FormModal>
    )
}

export default CreateSectionModal
